/**
 * FRONT-2321: "Edit" on the Items to save card — the draft and the pure
 * transforms on it. Shared by the desktop card and the phone's "Worth
 * saving?" card.
 *
 * A line can be reworded or removed, never added or moved to another target:
 * what goes back is `{ index, text }` per surviving ORIGINAL line, and the
 * server rebuilds the list from the stored proposal
 * (`src/agents/save-proposal-edit.ts`). Plain functions because the frontend
 * suite renders to static markup with no DOM — every transform a click
 * triggers is tested by calling the function the click calls.
 */
import type { SaveProposal, SaveProposalItem } from '~/hooks/queries/use-save-proposal';

export interface SaveListDraftLine {
  /** Position in the original proposal — also the stable React key. */
  index: number;
  /** Shown as the line's pill; never editable, never sent. */
  target: SaveProposalItem['target'];
  text: string;
}

export interface SaveListDraft {
  proposalId: number;
  lines: SaveListDraftLine[];
}

/** Mirrors the route's `edit` body (`/api/voice/save-proposal/decision`). */
export interface SaveListEdit {
  proposalId: number;
  lines: Array<{ index: number; text: string }>;
}

export function saveListDraftFrom(proposal: SaveProposal): SaveListDraft {
  return {
    proposalId: proposal.id,
    lines: proposal.items.map((item, index) => ({ index, target: item.target, text: item.text })),
  };
}

export function setSaveLineText(draft: SaveListDraft, index: number, text: string): SaveListDraft {
  return { ...draft, lines: draft.lines.map((l) => (l.index === index ? { ...l, text } : l)) };
}

export function removeSaveLine(draft: SaveListDraft, index: number): SaveListDraft {
  return { ...draft, lines: draft.lines.filter((l) => l.index !== index) };
}

/** Every line removed: nothing is left to save, so the honest action is Reject. */
export function saveListIsEmpty(draft: SaveListDraft): boolean {
  return draft.lines.length === 0;
}

/**
 * The one reason "Save this version" is unavailable, or `null` when it can be
 * sent — the disabled state and its sentence are one decision (FRONT-1670).
 */
export function saveListBlocker(draft: SaveListDraft): string | null {
  return draft.lines.some((l) => !l.text.trim()) ? "A line can't be empty. Remove it instead." : null;
}

export function saveListEditFrom(draft: SaveListDraft): SaveListEdit {
  return { proposalId: draft.proposalId, lines: draft.lines.map((l) => ({ index: l.index, text: l.text })) };
}

/** Pill label for a line's target — same words on the list and in the form. */
export function saveTargetLabel(target: SaveProposalItem['target']): string {
  return target === 'graph' ? 'Knowledge' : 'Note';
}
